import { api } from './client'

/**
 * Die gefahrene Strecke eines Fahrzeugs an einem Betriebstag — die Daten für die Karte
 * „Heute gefahren".
 *
 * <p>Die Positionen stehen nicht in der Portaldatenbank, sondern im Rohspeicher auf dem
 * Backend-Server; das Portal holt sie über die Backend-API. Zugeordnet wird ein Fahrzeug über
 * seine Telematik-Kennung. Ist keine gesetzt, antwortet der Server mit 422 und sagt, was fehlt.
 */

/** Ein Punkt der Strecke — die Protokollfelder, so wie das Gerät sie meldet. */
export interface Streckenpunkt {
  breite: number
  laenge: number
  gemessenAm: string
  geschwindigkeit: number | null
  richtung: number | null
  /** Kennung der Meldung im Rohspeicher — für einen späteren Sprung zur vollen Meldung. */
  kennung: string | null
}

export interface Tagesstrecke {
  telematikId: string
  /** Der Betriebstag als `YYYY-MM-DD`. */
  betriebstag: string
  anzahlPunkte: number
  vonAm: string | null
  bisAm: string | null
  /** Rohe Luftlinie zwischen aufeinanderfolgenden Punkten, GPS-Sprünge inbegriffen. */
  streckeKm: number
  hoechsteGeschwindigkeit: number | null
  kmGefahren: number | null
  /**
   * Die Nutzlast der jüngsten Zustandsmeldung des Tages — mit allen Feldern, die das Gerät
   * sendet, auch unbekannten. Deshalb ein offenes Objekt und keine feste Form.
   */
  letzterZustand: Record<string, unknown> | null
  punkte: Streckenpunkt[]
}

/**
 * Holt die Strecke eines Betriebstags. Ohne `betriebstag` liefert der Server den laufenden.
 *
 * @param betriebstag `YYYY-MM-DD` in der Lesart des Betriebstags (04:00–04:00)
 */
export function streckeHolen(fahrzeugId: number, betriebstag?: string): Promise<Tagesstrecke> {
  const abfrage = betriebstag ? `?datum=${encodeURIComponent(betriebstag)}` : ''
  return api.get<Tagesstrecke>(`/api/fahrzeuge/${fahrzeugId}/strecke${abfrage}`)
}

/**
 * Der zuletzt gemeldete Zustand eines Fahrzeugs — für die Box „Zuletzt gemeldet" in der
 * Fahrzeugakte, live aus der Backend-API.
 */
export interface AktuellerZustand {
  telematikId: string
  gemessenAm: string | null
  empfangenAm: string | null
  /** Alter der Meldung in Sekunden — damit ein stilles Gerät nicht wie „gerade gemeldet" wirkt. */
  alterSekunden: number | null
  /** Einschätzung der Backend-API, ob die Meldung noch frisch ist. */
  aktuell: boolean | null
  /** Volle Nutzlast mit allen Feldern, die das Gerät sendet (Position, Geschwindigkeit, km …). */
  inhalt: Record<string, unknown> | null
}

/**
 * Holt den letzten Zustand aus der Backend-API. Liefert `null`, wenn dem Fahrzeug kein Gerät
 * zugeordnet ist, kein Backend-Zugang konfiguriert ist oder (noch) nichts gemeldet wurde — der
 * Server antwortet dann mit `204`, und die Box bleibt wie bisher.
 */
export async function letzterZustandHolen(fahrzeugId: number): Promise<AktuellerZustand | null> {
  const z = await api.get<AktuellerZustand | undefined>(
    `/api/fahrzeuge/${fahrzeugId}/letzter-zustand`,
  )
  return z ?? null
}

/**
 * Ein einzelner PGN-Eintrag aus dem Bus-Inventar — roh wie ihn das Gerät meldet (die Deutung
 * macht der PGN-Katalog `komponenten/pgn.ts`, nicht der Server).
 */
export interface BusInventarPgn {
  /** Parameter Group Number (dezimal). */
  pgn: number
  /** Der aufgezeichnete 29-Bit-CAN-Bezeichner als Hex. */
  id: string
  /** Gesehene Rahmen im Beobachtungsfenster. */
  n: number
  /** Senderate in Hz. */
  hz: number
  /** Quelladresse(n), unter denen die PGN gesehen wurde. */
  sa: number[]
  /** Zuletzt gesehene Nutzbytes als Hex. */
  byte: string
  /** Ob das Gerät die PGN dekodiert (bekannt). */
  bek: boolean
  /** Ob es eine Transportprotokoll-PGN ist (Mehrpaket). */
  tp: boolean
}

/**
 * Das Bus-Inventar eines Fahrzeugs: welche PGNs der Fahrzeugbus sendet (BUS_INVENTAR), roh aus
 * der Backend-API. Für die Karte „Bus-Inventar" auf der Streckenseite; die Bedeutung je PGN
 * kommt aus dem PGN-Katalog.
 */
export interface BusInventar {
  telematikId: string
  gemessenAm: string | null
  empfangenAm: string | null
  alterSekunden: number | null
  /** Anzahl verschiedener PGNs im Fenster. */
  pgnAnzahl: number | null
  /** Davon vom Gerät dekodiert (bekannt). */
  pgnBekannt: number | null
  /** Davon (noch) nicht dekodiert. */
  pgnUnbekannt: number | null
  /** Rahmen gesamt im Beobachtungsfenster. */
  rahmenGesamt: number | null
  /** Länge des Beobachtungsfensters in Sekunden. */
  fensterSekunden: number | null
  pgns: BusInventarPgn[] | null
}

/**
 * Holt die jüngste Bus-Inventar-Meldung. Liefert `null`, wenn dem Fahrzeug kein Gerät
 * zugeordnet ist, kein Backend-Zugang konfiguriert ist oder (noch) kein Inventar gemeldet
 * wurde — der Server antwortet dann mit `204`.
 */
export async function busInventarHolen(fahrzeugId: number): Promise<BusInventar | null> {
  const i = await api.get<BusInventar | undefined>(
    `/api/fahrzeuge/${fahrzeugId}/bus-inventar`,
  )
  return i ?? null
}
